import { useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { fmtGB } from "@/lib/localServer";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { HardDrive, Check, Loader2 } from "lucide-react";

function DriveRow({ d, active, onPick }) {
  const pct = Math.min(100, d.percent);
  return (
    <button type="button" onClick={() => onPick(d.mountpoint)} data-testid={`drive-option-${d.mountpoint}`}
      className={`w-full text-left border p-3 flex items-center gap-3 transition-colors ${active ? "border-primary bg-primary/10" : "border-border hover:bg-muted/40"}`}>
      <HardDrive className={`w-5 h-5 shrink-0 ${active ? "text-primary" : "text-muted-foreground"}`} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <span className="font-mono text-sm truncate">{d.mountpoint}</span>
          <span className="text-xs text-muted-foreground shrink-0">{fmtGB(d.free)} free of {fmtGB(d.total)}</span>
        </div>
        <div className="h-1.5 bg-muted mt-2"><div className={`h-full ${pct > 90 ? "bg-destructive" : "bg-primary"}`} style={{ width: `${pct}%` }} /></div>
      </div>
      {active && <Check className="w-4 h-4 text-primary shrink-0" />}
    </button>
  );
}

export function DrivePicker({ onSaved, initialFolder = "SynqMotoData" }) {
  const [drives, setDrives] = useState(null);
  const [pick, setPick] = useState("");
  const [folder, setFolder] = useState(initialFolder);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/local/drives").then(({ data }) => {
      setDrives(data.drives);
      const cur = data.drives.find(d => data.current && data.current.startsWith(d.mountpoint));
      setPick(cur?.mountpoint || data.drives[0]?.mountpoint || "");
    }).catch(e => { toast.error(formatErr(e)); setDrives([]); });
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/local/storage", { mountpoint: pick, folder });
      toast.success(data.restart_required ? "Saved — restart the shop server to move data there" : "Storage location saved");
      onSaved?.(data);
    } catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };

  if (!drives) return <div className="text-sm text-muted-foreground flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Reading drives…</div>;
  return (
    <div className="space-y-4" data-testid="drive-picker">
      <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
        {drives.length === 0 && <div className="text-sm text-muted-foreground">No usable drives found.</div>}
        {drives.map(d => <DriveRow key={d.mountpoint} d={d} active={pick === d.mountpoint} onPick={setPick} />)}
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="storage-folder" className="text-xs uppercase tracking-[0.15em]">Folder on this drive</Label>
        <Input id="storage-folder" data-no-uppercase="" data-testid="storage-folder-input" value={folder} onChange={e => setFolder(e.target.value)} placeholder="SynqMotoData" />
        <div className="text-xs text-muted-foreground font-mono">{pick ? `${pick.replace(/[\\/]$/, "")}/${folder}` : ""}</div>
      </div>
      <Button onClick={save} disabled={!pick || !folder.trim() || busy} data-testid="storage-save-btn">
        {busy && <Loader2 className="w-4 h-4 animate-spin mr-2" />}Use this location
      </Button>
    </div>
  );
}
